/**
 * Data-source provenance badges — transparency layer for the "which source
 * powered this data point?" question.
 *
 * Two exports:
 *   - <SourceBadge source="realie|rentcast|zillow" />
 *       Tiny inline pill for cards / metric rows.
 *   - <DataSourcesFooter dataSources={analysis.data_sources} property={...} />
 *       Compact footer line for the analysis detail view:
 *         "Sources: Realie · RentCast — Updated 2 days ago"
 *
 * Wording rules (per PM):
 *   - Never overstate certainty. Avoid "verified" unless the source truly
 *     verifies the field.
 *   - Rent from RentCast → "estimate", not "verified".
 *   - Rent from a listing description → "actual tenant rent" (that IS verified).
 */
import { useMemo } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";

// --- Source registry — the single source of truth for label + copy --------
const SOURCE_META = {
  realie: {
    label: "Realie",
    short: "Property data: Realie",
    tooltip:
      "Realie provides property/parcel data (address, sqft, tax assessments, sale history).",
  },
  zillow: {
    label: "Zillow",
    short: "Property data: Zillow",
    tooltip:
      "Zillow MLS listing data (address, price, beds/baths, sqft, listing photos).",
  },
  rentcast: {
    label: "RentCast",
    short: "Rent estimate: RentCast",
    tooltip:
      "RentCast provides rent estimates and rental-market comps. Estimates may vary from actual market conditions.",
  },
  mock: {
    label: "Sample Data",
    short: "Sample data (demo)",
    tooltip: "Demo/sample data — used when live APIs are unavailable.",
  },
  listing_description: {
    label: "Listing text",
    short: "Rent: Listing description",
    tooltip:
      "Rent extracted directly from the listing description — this is the actual tenant rent.",
  },
  zillow_zestimate: {
    label: "Zillow Estimate",
    short: "Rent: Zillow rent estimate",
    tooltip: "Zillow's algorithmic rent estimate (rentZestimate).",
  },
  zip_comparable: {
    label: "Local comps",
    short: "Rent: Local comparable rentals",
    tooltip:
      "Median rent from comparable rentals in the same ZIP code.",
  },
  state_average: {
    label: "State average",
    short: "Rent: State average",
    tooltip:
      "State-level average rent for this bed count. Lowest confidence — used only when property-specific data is unavailable.",
  },
  zillow_actual: {
    label: "Zillow",
    short: "Zillow actual",
    tooltip: "Actual value from the Zillow property record.",
  },
  state_rate: {
    label: "State rate",
    short: "State average tax rate",
    tooltip: "Estimated using the state's average property tax rate.",
  },
  industry_standard: {
    label: "Industry standard",
    short: "Industry-standard assumption",
    tooltip: "Standard industry assumption (not a live data source).",
  },
  rule_of_thumb: {
    label: "Rule of thumb",
    short: "Standard rule-of-thumb",
    tooltip: "Common investor rule-of-thumb (not a live data source).",
  },
};

const getMeta = (sourceKey) => {
  if (!sourceKey) return null;
  const key = String(sourceKey).toLowerCase();
  return SOURCE_META[key] || { label: key, short: key, tooltip: "" };
};

// --- Relative-time helper (small, dependency-free) ------------------------
const relativeTime = (iso) => {
  if (!iso) return null;
  try {
    const diffSec = (Date.now() - new Date(iso).getTime()) / 1000;
    if (Number.isNaN(diffSec) || diffSec < 0) return null;
    if (diffSec < 60) return "just now";
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
    const days = Math.floor(diffSec / 86400);
    if (days === 1) return "yesterday";
    if (days < 30) return `${days} days ago`;
    const months = Math.floor(days / 30);
    if (months < 12) return `${months}mo ago`;
    return `${Math.floor(months / 12)}y ago`;
  } catch {
    return null;
  }
};

// --- Compact inline badge (cards + metric rows) ---------------------------
export const SourceBadge = ({ source, className = "" }) => {
  const meta = getMeta(source);
  if (!meta) return null;
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            data-testid={`source-badge-${String(source).toLowerCase()}`}
            className={`inline-flex items-center text-[9px] font-medium px-1.5 py-0.5 rounded-full border border-border/60 bg-secondary/60 text-muted-foreground ${className}`}
          >
            {meta.label}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[240px] text-[11px] leading-snug">
          {meta.tooltip || meta.short}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// --- Aggregate confidence chip -------------------------------------------
// Renders:  [ ● Data confidence: High ]  with hover breakdown by field.
const CONFIDENCE_STYLE = {
  high: {
    label: "High",
    pill: "bg-emerald-500/10 text-emerald-500 border-emerald-500/25",
    dot: "bg-emerald-500",
  },
  medium: {
    label: "Medium",
    pill: "bg-amber-500/10 text-amber-500 border-amber-500/25",
    dot: "bg-amber-500",
  },
  low: {
    label: "Low",
    pill: "bg-red-500/10 text-red-400 border-red-500/25",
    dot: "bg-red-500",
  },
};

const CONFIDENCE_BLURB = {
  high: "Live property + rent data with verified taxes/insurance. Numbers are as trustworthy as we can make them.",
  medium: "Some inputs are estimated (rent comps or state averages). Verify before making offers.",
  low: "Multiple inputs are rough estimates. Use as a directional screen only.",
};

export const ConfidenceChip = ({ confidence, className = "", size = "sm" }) => {
  if (!confidence || !confidence.level) return null;
  const level = String(confidence.level).toLowerCase();
  const style = CONFIDENCE_STYLE[level];
  if (!style) return null;

  const factors = Array.isArray(confidence.factors) ? confidence.factors : [];
  const sizing =
    size === "xs"
      ? "text-[9px] px-1.5 py-0.5"
      : "text-[10px] px-2 py-0.5";

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            data-testid={`confidence-chip-${level}`}
            className={`inline-flex items-center gap-1 rounded-full border font-semibold ${style.pill} ${sizing} ${className}`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
            <span className="uppercase tracking-wider">Data</span>
            <span>·</span>
            <span>{style.label}</span>
          </span>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className="max-w-[280px] text-[11px] leading-relaxed"
        >
          <p className="font-semibold text-foreground mb-1">
            Data confidence: {style.label}
          </p>
          <p className="text-muted-foreground mb-2">{CONFIDENCE_BLURB[level]}</p>
          {factors.length > 0 && (
            <div className="space-y-1 border-t border-border/40 pt-2">
              {factors.map((f) => {
                const fLevel = String(f.level || "medium").toLowerCase();
                const fStyle =
                  CONFIDENCE_STYLE[fLevel] || CONFIDENCE_STYLE.medium;
                return (
                  <div
                    key={f.field}
                    className="flex items-start gap-2"
                    data-testid={`confidence-factor-${f.field}`}
                  >
                    <span
                      className={`w-1.5 h-1.5 rounded-full mt-1 shrink-0 ${fStyle.dot}`}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="capitalize font-medium text-foreground">
                          {f.field}
                        </span>
                        <span className="text-muted-foreground text-[10px] uppercase tracking-wider">
                          {fStyle.label}
                        </span>
                      </div>
                      <p className="text-muted-foreground truncate">{f.label}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// --- Detail-page footer line ---------------------------------------------
// Renders:  "Sources: Realie · RentCast — Rent updated 2 days ago"
export const DataSourcesFooter = ({ dataSources, property, className = "" }) => {
  const items = useMemo(() => {
    const rows = [];
    const seen = new Set();

    // Property provenance (prefer analysis-level, fall back to the listing).
    const propSrc =
      dataSources?.property?.source ||
      (property?.listing_source ? String(property.listing_source).toLowerCase() : null);
    const propFetched =
      dataSources?.property?.fetched_at || property?.data_fetched_at || null;
    if (propSrc && !seen.has(propSrc)) {
      const meta = getMeta(propSrc);
      if (meta) {
        rows.push({
          key: `property-${propSrc}`,
          role: "Property data",
          source: propSrc,
          label: meta.label,
          tooltip: meta.tooltip,
          fetchedAt: propFetched,
        });
        seen.add(propSrc);
      }
    }

    // Rent provenance
    const rentSrc = dataSources?.rent?.source;
    if (rentSrc) {
      const meta = getMeta(rentSrc);
      if (meta) {
        // Show RentCast as its own line item (not deduped) since it's a
        // distinct vendor even if the property came from Zillow/Realie.
        const dedupeKey =
          rentSrc === "rentcast" ? "rent-rentcast" : `rent-${rentSrc}`;
        if (!seen.has(dedupeKey)) {
          rows.push({
            key: dedupeKey,
            role: "Rent estimate",
            source: rentSrc,
            label: rentSrc === "rentcast" ? "RentCast" : meta.label,
            tooltip: meta.tooltip,
            fetchedAt: dataSources?.rent?.fetched_at || null,
            compCount: dataSources?.rent?.comp_count || 0,
            confidence: dataSources?.rent?.confidence,
          });
          seen.add(dedupeKey);
        }
      }
    }

    return rows;
  }, [dataSources, property]);

  if (items.length === 0) return null;

  const labelList = items.map((i) => i.label).join(" · ");
  // Prefer the freshest fetched_at for the "updated N ago" hint on the footer.
  const latestFetched = items
    .map((i) => i.fetchedAt)
    .filter(Boolean)
    .sort()
    .slice(-1)[0];
  const rel = relativeTime(latestFetched);

  return (
    <div
      data-testid="data-sources-footer"
      className={`flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px] text-muted-foreground ${className}`}
    >
      <span className="uppercase tracking-wider text-[9px] font-semibold text-muted-foreground/80">
        Sources
      </span>
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              data-testid="data-sources-list"
              className="cursor-help underline underline-offset-2 decoration-dotted decoration-muted-foreground/40"
            >
              {labelList}
            </span>
          </TooltipTrigger>
          <TooltipContent
            side="top"
            className="max-w-[280px] text-[11px] leading-relaxed"
          >
            <div className="space-y-1.5">
              {items.map((i) => (
                <div key={i.key}>
                  <span className="font-semibold text-foreground">
                    {i.role}: {i.label}
                  </span>
                  <p className="text-muted-foreground">{i.tooltip}</p>
                </div>
              ))}
              <p className="text-muted-foreground/70 italic pt-1 border-t border-border/40">
                Estimates may vary from actual market conditions.
              </p>
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      {rel && (
        <span data-testid="data-sources-freshness" className="text-muted-foreground/70">
          · Updated {rel}
        </span>
      )}
    </div>
  );
};

export default DataSourcesFooter;
